import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import { Upload } from "lucide-react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { Badge } from "@/components/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@ndd/ui";
import { cn } from "@/lib/utils";
import { AdminGuard, AdminNav } from "./shared";
import type { AlwaysAiRole } from "../../../../shared/api-types";

const DATE_TIME = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const DATE_ONLY = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "short",
  day: "numeric",
});

const PREVIEW_LIMIT = 5;

type UserRole = AlwaysAiRole;
type SessionStatus = "pending" | "analyzing" | "complete" | "error";

interface CoverageAuditRow {
  sessionId: string;
  shortId: string;
  rdId: string;
  rdSlug: string;
  rdName: string;
  sessionDate: string | null;
  uploadedAt: string;
  status: SessionStatus;
  uploaderName: string;
  uploaderRole: UserRole | null;
}

interface CoverageAuditPage {
  rows: CoverageAuditRow[];
  page: number;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface CoverageIssueRow {
  id: string;
  rdId: string;
  rdSlug: string;
  rdName: string;
  title: string;
  detail: string;
  occurredAt: string | null;
}

interface CoverageExceptionList {
  rows: CoverageIssueRow[];
  totalCount: number;
  rowLimit: number;
}

interface UploaderRoleMixRow {
  role: UserRole;
  label: string;
  uploads: number;
  percent: number;
}

interface UploaderMixRow {
  userId: string;
  name: string;
  email: string;
  role: UserRole | null;
  uploads: number;
  rdsCovered: number;
  issueCount: number;
  mostRecentUploadAt: string;
  topRdNames: string[];
}

interface AdminCoverageDashboard {
  windowDays: number;
  noRecentDays: number;
  auditLog: CoverageAuditPage;
  issues: CoverageExceptionList;
  noRecentRds: CoverageExceptionList;
  uploaderMix: {
    totalUploads: number;
    roleRows: UploaderRoleMixRow[];
    uploaderRows: UploaderMixRow[];
  };
}

export function AdminCoverage(): ReactElement {
  const { getToken } = useAuth();
  const search = useSearch({ strict: false }) as { auditPage?: number | string };
  const auditPage = sanitizeAuditPage(search.auditPage);
  const coverage = useQuery({
    queryKey: ["admin-coverage", auditPage],
    queryFn: () =>
      authenticatedApi<{ data: AdminCoverageDashboard }>(
        getToken,
        `/api/admin/coverage?auditPage=${auditPage}`
      ),
  });

  if (coverage.error) {
    const error = coverage.error;

    if (error instanceof ApiRequestError) {
      return (
        <AdminGuard>
          <div className="min-h-screen bg-background flex items-center justify-center">
            <p className="text-lg text-muted-foreground">
              {error.status} - {apiErrorMessage(error)}
            </p>
          </div>
        </AdminGuard>
      );
    }

    return (
      <AdminGuard>
        <div className="max-w-[1200px] mx-auto px-6 py-8">
          <p className="text-sm text-muted-foreground">Unable to load coverage.</p>
        </div>
      </AdminGuard>
    );
  }

  if (!coverage.data) {
    return (
      <AdminGuard>
        <div className="max-w-[1200px] mx-auto px-6 py-8">
          <p className="text-sm text-muted-foreground">Loading coverage...</p>
        </div>
      </AdminGuard>
    );
  }

  return (
    <AdminGuard>
      <CoverageView dashboard={coverage.data.data} />
    </AdminGuard>
  );
}

function CoverageView({ dashboard }: { dashboard: AdminCoverageDashboard }) {
  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      <AdminNav />
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <div className="text-caption uppercase tracking-wider text-muted-foreground font-medium">
            Admin
          </div>
          <h1 className="text-2xl font-bold font-sans text-foreground">Coverage</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-[720px]">
            Transcript ingestion and RD coverage health. Coverage uses upload time, not
            supervisor usage activity.
          </p>
        </div>
      </div>

      <CoverageSection
        title="Audit log"
        description="Recent transcript uploads. Upload time comes from sessions.createdAt."
      >
        {dashboard.auditLog.rows.length === 0 ? (
          <EmptySectionText>No transcript uploads yet.</EmptySectionText>
        ) : (
          <div>
            {dashboard.auditLog.rows.map((row) => (
              <AuditRow key={row.sessionId} row={row} />
            ))}
            <AuditPagination auditLog={dashboard.auditLog} />
          </div>
        )}
      </CoverageSection>

      <CoverageExceptionsPanel
        issues={dashboard.issues}
        noRecentRds={dashboard.noRecentRds}
        noRecentDays={dashboard.noRecentDays}
      />

      <CoverageSection
        title="Uploader mix"
        description={`All transcript uploads in the last ${dashboard.windowDays} days.`}
        className="mt-4"
      >
        <UploaderMix dashboard={dashboard} />
      </CoverageSection>
    </div>
  );
}

function CoverageSection({
  title,
  description,
  className,
  children,
}: {
  title: string;
  description: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border border-border bg-card shadow-sm overflow-hidden",
        className
      )}
    >
      <div className="px-5 py-4 bg-muted border-b border-border">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="text-label text-muted-foreground mt-1">{description}</p>
      </div>
      <div>{children}</div>
    </section>
  );
}

function EmptySectionText({ children }: { children: ReactNode }) {
  return <div className="px-5 py-8 text-sm text-muted-foreground">{children}</div>;
}

function AuditRow({ row }: { row: CoverageAuditRow }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_150px_110px] gap-3 px-5 py-4 border-b border-sand-100 last:border-b-0 items-center">
      <div className="min-w-0">
        <Link
          to="/rds/$rdId/sessions/$sessionId"
          params={{ rdId: row.rdId, sessionId: row.shortId }}
          className="text-body font-semibold text-foreground hover:underline underline-offset-4"
        >
          {row.rdName} transcript added
        </Link>
        <div className="text-label text-muted-foreground mt-0.5 truncate">
          {row.uploaderName}, {roleLabel(row.uploaderRole)}
          {" - "}
          uploaded {formatDateTime(row.uploadedAt)}
          {row.sessionDate ? ` - session ${formatDate(row.sessionDate)}` : ""}
        </div>
      </div>
      <div className="text-label text-muted-foreground md:text-right">
        {row.shortId}
      </div>
      <StatusText status={row.status} />
    </div>
  );
}

function AuditPagination({ auditLog }: { auditLog: CoverageAuditPage }) {
  if (!auditLog.hasPreviousPage && !auditLog.hasNextPage) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-sand-100 px-5 py-3">
      <div className="text-label text-muted-foreground">Page {auditLog.page}</div>
      <div className="flex items-center gap-2">
        <AuditPageLink page={auditLog.page - 1} disabled={!auditLog.hasPreviousPage}>
          Previous
        </AuditPageLink>
        <AuditPageLink page={auditLog.page + 1} disabled={!auditLog.hasNextPage}>
          Next
        </AuditPageLink>
      </div>
    </div>
  );
}

function AuditPageLink({
  page,
  disabled,
  children,
}: {
  page: number;
  disabled: boolean;
  children: ReactNode;
}) {
  const className =
    "rounded-full border border-border px-3 py-1 text-label font-semibold text-sand-600 hover:bg-sand-50";

  if (disabled) {
    return (
      <span className={cn(className, "pointer-events-none opacity-45")}>
        {children}
      </span>
    );
  }

  return (
    <Link to="/admin/coverage" search={{ auditPage: page }} className={className}>
      {children}
    </Link>
  );
}

function StatusText({ status }: { status: CoverageAuditRow["status"] }) {
  switch (status) {
    case "complete":
      return <AuditStatusBadge variant="great" label="Complete" />;
    case "error":
      return <AuditStatusBadge variant="error-status" label="Error" />;
    case "analyzing":
      return <AuditStatusBadge variant="analyzing" label="Analyzing" />;
    case "pending":
      return <AuditStatusBadge variant="queued" label="Pending" />;
  }
}

function AuditStatusBadge({
  variant,
  label,
}: {
  variant: "great" | "error-status" | "analyzing" | "queued";
  label: string;
}) {
  return <CoverageStatusBadge variant={variant} label={label} />;
}

function CoverageStatusBadge({
  variant,
  label,
}: {
  variant: "great" | "error-status" | "analyzing" | "queued" | "muted";
  label: string;
}) {
  return (
    <Badge
      variant={variant}
      size="md"
      className="h-[30px] w-fit px-3 py-0 text-label font-semibold leading-none md:justify-self-end"
    >
      {label}
    </Badge>
  );
}

function CoverageExceptionsPanel({
  issues,
  noRecentRds,
  noRecentDays,
}: {
  issues: CoverageExceptionList;
  noRecentRds: CoverageExceptionList;
  noRecentDays: number;
}) {
  return (
    <section className="mt-4 rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-muted border-b border-border">
        <h2 className="text-base font-semibold text-foreground">Coverage exceptions</h2>
        <p className="text-label text-muted-foreground mt-1">
          Rows that may need retry, review, uploader follow-up, or RD coverage
          attention.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        <ExceptionColumn
          title="Transcripts with issues"
          description="Uploads that may need retry, review, or follow-up."
          emptyText="No transcript issues found."
          rows={issues}
          modalTitle="Transcripts with issues"
          modalDescription="All uploads currently marked for retry, review, or follow-up."
        />
        <ExceptionColumn
          title="No recent transcripts"
          description={`RDs with no transcript uploads in the last ${noRecentDays} days.`}
          emptyText="Every RD has a recent upload."
          rows={noRecentRds}
          modalTitle="No recent transcripts"
          modalDescription={`All RDs without transcript uploads in the last ${noRecentDays} days.`}
        />
      </div>
    </section>
  );
}

function ExceptionColumn({
  title,
  description,
  emptyText,
  rows,
  modalTitle,
  modalDescription,
}: {
  title: string;
  description: string;
  emptyText: string;
  rows: CoverageExceptionList;
  modalTitle: string;
  modalDescription: string;
}) {
  const previewRows = rows.rows.slice(0, PREVIEW_LIMIT);
  const hasOverflow = rows.totalCount > PREVIEW_LIMIT;

  return (
    <div className="min-w-0 border-b border-border lg:border-b-0 lg:border-r lg:last:border-r-0">
      <div className="px-5 py-3 bg-background border-b border-border">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-body font-semibold text-foreground">{title}</h3>
          <span className="text-label font-semibold text-muted-foreground">
            {rows.totalCount.toLocaleString()}
          </span>
        </div>
        <p className="text-label text-muted-foreground mt-0.5">{description}</p>
      </div>

      {rows.totalCount === 0 ? (
        <div className="px-5 py-8 text-sm text-muted-foreground">{emptyText}</div>
      ) : (
        <>
          <div className="divide-y divide-sand-100">
            {previewRows.map((issue) => (
              <ExceptionRow key={issue.id} issue={issue} />
            ))}
          </div>
          {hasOverflow && (
            <div className="border-t border-sand-100 px-5 py-3">
              <ExceptionModal
                title={modalTitle}
                description={modalDescription}
                exceptionList={rows}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExceptionModal({
  title,
  description,
  exceptionList,
}: {
  title: string;
  description: string;
  exceptionList: CoverageExceptionList;
}) {
  const isCapped = exceptionList.totalCount > exceptionList.rows.length;
  const loadedCount = exceptionList.rows.length;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-label font-semibold text-foreground underline-offset-4 hover:underline"
        >
          {isCapped
            ? `View first ${loadedCount.toLocaleString()} of ${exceptionList.totalCount.toLocaleString()}`
            : `View all ${exceptionList.totalCount.toLocaleString()}`}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-[760px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 py-4 bg-muted border-b border-border">
          <DialogTitle className="text-base text-foreground">{title}</DialogTitle>
          <DialogDescription className="text-label text-muted-foreground">
            {description}
            {isCapped
              ? ` Showing first ${loadedCount.toLocaleString()} of ${exceptionList.totalCount.toLocaleString()}.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[min(620px,70vh)] overflow-y-auto divide-y divide-sand-100">
          {exceptionList.rows.map((issue) => (
            <ExceptionRow key={issue.id} issue={issue} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExceptionRow({ issue }: { issue: CoverageIssueRow }) {
  return (
    <Link
      to="/rds/$rdId"
      params={{ rdId: issue.rdId }}
      className="block px-5 py-3 hover:bg-sand-50/70"
    >
      <span className="min-w-0">
        <span className="block text-body font-semibold text-foreground truncate">
          {issue.title}
        </span>
        <span className="block text-label text-muted-foreground mt-0.5 truncate">
          {issue.detail}
        </span>
      </span>
    </Link>
  );
}

function UploaderMix({ dashboard }: { dashboard: AdminCoverageDashboard }) {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-b border-border">
        {dashboard.uploaderMix.roleRows.map((row) => (
          <RoleMixCell key={row.role} row={row} />
        ))}
      </div>

      <div className="hidden md:grid grid-cols-[minmax(0,1.5fr)_110px_100px_120px_100px_130px] gap-4 px-5 py-3 bg-background border-b border-border text-caption uppercase tracking-wider text-muted-foreground font-medium">
        <div>Uploader</div>
        <div>Role</div>
        <div className="text-right">Uploads</div>
        <div className="text-right">RDs covered</div>
        <div className="text-right">Issues</div>
        <div>Last upload</div>
      </div>

      {dashboard.uploaderMix.uploaderRows.length === 0 ? (
        <EmptySectionText>No uploads in this window.</EmptySectionText>
      ) : (
        dashboard.uploaderMix.uploaderRows.map((row) => (
          <UploaderRow key={row.userId} row={row} />
        ))
      )}
    </div>
  );
}

function RoleMixCell({ row }: { row: UploaderRoleMixRow }) {
  return (
    <div className="px-5 py-4 border-b border-sand-100 sm:border-r sm:last:border-r-0 lg:border-b-0">
      <div className="flex items-center gap-2 text-caption uppercase tracking-wider text-muted-foreground font-medium">
        <Upload className="h-3.5 w-3.5" />
        {row.label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-foreground">
        {row.uploads.toLocaleString()}
      </div>
      <div className="text-label text-muted-foreground">
        {row.percent}% of transcript flow
      </div>
    </div>
  );
}

function UploaderRow({ row }: { row: UploaderMixRow }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_110px_100px_120px_100px_130px] gap-3 md:gap-4 px-5 py-4 border-b border-sand-100 last:border-b-0 items-center">
      <div className="min-w-0">
        <div className="text-body font-semibold text-foreground truncate">
          {row.name}
        </div>
        <div className="text-label text-muted-foreground truncate">
          {row.topRdNames.length > 0
            ? `Most active on ${row.topRdNames.join(", ")}`
            : row.email}
        </div>
      </div>
      <MobileMetric label="Role" value={roleLabel(row.role)} />
      <MobileMetric label="Uploads" value={row.uploads.toLocaleString()} align />
      <MobileMetric label="RDs covered" value={row.rdsCovered.toLocaleString()} align />
      <MobileMetric label="Issues" value={row.issueCount.toLocaleString()} align />
      <div className="text-label text-muted-strong">
        <span className="md:hidden block text-micro uppercase tracking-wider text-subtle font-medium">
          Last upload
        </span>
        {formatDateTime(row.mostRecentUploadAt)}
      </div>
    </div>
  );
}

function MobileMetric({
  label,
  value,
  align = false,
}: {
  label: string;
  value: string;
  align?: boolean;
}) {
  return (
    <div
      className={cn(
        "text-body font-semibold text-foreground",
        align && "md:text-right"
      )}
    >
      <span className="md:hidden block text-micro uppercase tracking-wider text-subtle font-medium">
        {label}
      </span>
      {value}
    </div>
  );
}

// Null means the registry currently grants this person no AlwaysAI role at all,
// which the old `users.role` column could not express. "No role" is the honest
// label; naming one would claim access the person does not have.
function roleLabel(role: UserRole | null): string {
  switch (role) {
    case "admin":
      return "Admin";
    case "clinical_admin":
      return "Clinical admin";
    case "supervisor":
      return "Supervisor";
    case "va":
      return "VA";
    case "rd":
      return "RD";
    default:
      return "No role";
  }
}

function formatDateTime(value: string) {
  return DATE_TIME.format(new Date(value));
}

function formatDate(value: string) {
  return DATE_ONLY.format(new Date(value));
}

function sanitizeAuditPage(value: number | string | undefined): number {
  const page = Number(value);
  if (!page || !Number.isFinite(page)) return 1;

  return Math.max(1, Math.floor(page));
}

function apiErrorMessage(error: ApiRequestError): string {
  const body = error.body as { error?: string } | undefined;

  return body?.error ?? error.message;
}
